import { AlertCircle, Check, Loader2, X } from "lucide-react";

export type CommandFeedbackState =
  | { status: "idle" }
  | { status: "pending" }
  | { status: "succeeded" }
  | { status: "failed"; error: string };

export function CommandFeedback({
  state,
  onReset,
  pendingMessage,
  successMessage,
  testId,
  announce = true,
}: {
  state: CommandFeedbackState;
  onReset: () => void;
  // Required, with no default: a shared "Saving…"/"Saved" default is how a
  // delete once reported "Saved". Command callers get these from
  // commandWording() through AdminCommandFeedback or GovernanceCommandFeedback.
  pendingMessage: string;
  successMessage: string;
  testId?: string;
  /**
   * False when the caller already announces these texts in a region of its
   * own, so a screen reader does not read them twice.
   */
  announce?: boolean;
}) {
  if (state.status === "idle") return null;

  if (state.status === "pending") {
    return (
      <div
        role={announce ? "status" : undefined}
        aria-live={announce ? "polite" : undefined}
        data-testid={testId ? `${testId}-pending` : undefined}
        className="flex items-center gap-2 text-sm text-muted-foreground"
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        {pendingMessage}
      </div>
    );
  }

  const failed = state.status === "failed";
  return (
    <div
      role={announce ? "status" : undefined}
      aria-live={announce ? (failed ? "assertive" : "polite") : undefined}
      data-testid={
        testId ? `${testId}-${failed ? "failed" : "succeeded"}` : undefined
      }
      className={
        failed
          ? "flex items-center gap-2 rounded-lg border border-status-error bg-status-error/15 px-3 py-2 text-sm text-foreground"
          : "flex items-center gap-2 rounded-lg border border-status-success bg-status-success/15 px-3 py-2 text-sm text-foreground"
      }
    >
      {failed ? (
        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
      ) : (
        <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
      )}
      {failed ? state.error : successMessage}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onReset}
        className="ml-auto flex shrink-0 items-center justify-center rounded-sm max-sm:-my-2 max-sm:-mr-2 max-sm:size-11 text-current hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-2"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
